import { useEffect, useRef, useState } from 'react';
import { architectApi, Pipeline } from './api';

/** Resume only a missing current step. Opening an existing project should
 * continue the conveyor just like a new approval, without re-approving or
 * copying an older document into the current revision. */
export function pipelineResumeStep(pipeline: Pipeline | null | undefined): 'estimate' | 'pricing' | 'proposal' | null {
  if (!pipeline || pipeline.architecture !== 'approved' || pipeline.running?.architecture) return null;
  if (pipeline.estimate === 'none') return pipeline.running?.estimate ? null : 'estimate';
  if (pipeline.estimate !== 'approved') return null;
  // A legacy project can report a pricing draft before the sheet exists.
  // The server checks the saved revision and preserves any existing edits.
  if (pipeline.pricing === 'none' || pipeline.pricing === 'draft') return 'pricing';
  const pricingApproved = pipeline.pricing === 'approved' || (pipeline.pricing === undefined && pipeline.priced);
  return pricingApproved && pipeline.proposal === 'none' && !pipeline.running?.proposal ? 'proposal' : null;
}

export function usePipelineResume({ projectId, pipeline, revision, enabled, onRefresh }: {
  projectId: string;
  pipeline: Pipeline | null | undefined;
  /** Current architecture approval/version; metadata edits do not restart it. */
  revision: string;
  enabled: boolean;
  onRefresh: (pipeline: Pipeline, started: 'estimate' | 'pricing' | 'proposal' | null) => void | Promise<void>;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(new Set<string>());
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;
  const step = pipelineResumeStep(pipeline);
  const key = `${projectId}:${revision}:${step}`;
  const identity = useRef(`${projectId}:${revision}`);
  identity.current = `${projectId}:${revision}`;

  useEffect(() => {
    if (!enabled || !projectId || !step || attempted.current.has(key)) return;
    attempted.current.add(key);
    const requestedIdentity = `${projectId}:${revision}`;
    setBusyKey(key);
    setError(null);
    void architectApi.resumePipeline(projectId).then(async result => {
      if (!mounted.current || identity.current !== requestedIdentity) return;
      if (result.resume_reason === 'failed') setError('Automatic preparation failed. Retry the current step.');
      await refresh.current(result.pipeline, result.auto_started);
    }).catch((err: unknown) => {
      if (mounted.current && identity.current === requestedIdentity) setError((err as Error).message || 'Could not continue the current approval sequence.');
    }).finally(() => {
      if (mounted.current) setBusyKey(current => current === key ? null : current);
    });
  }, [enabled, projectId, revision, step, key]);

  return { busy: busyKey === key, error };
}
